import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { parseAuthError } from '@/lib/utils';
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
export default function Login() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const handleToken = useCallback((token) => setCaptchaToken(token), []);
    const handleExpire = useCallback(() => setCaptchaToken(null), []);
    useEffect(() => {
        if (!SITE_KEY)
            return;
        if (!document.getElementById('cf-turnstile-script')) {
            const script = document.createElement('script');
            script.id = 'cf-turnstile-script';
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
            script.async = true;
            document.head.appendChild(script);
        }
        let intervalId;
        function tryRender() {
            const ts = window.turnstile;
            if (ts && containerRef.current && !widgetIdRef.current) {
                clearInterval(intervalId);
                const id = ts.render(containerRef.current, {
                    sitekey: SITE_KEY,
                    callback: handleToken,
                    'expired-callback': handleExpire,
                    'error-callback': handleExpire,
                });
                if (id !== undefined)
                    widgetIdRef.current = id;
            }
        }
        intervalId = setInterval(tryRender, 100);
        return () => {
            clearInterval(intervalId);
            const ts = window.turnstile;
            if (ts && widgetIdRef.current) {
                ts.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, [handleToken, handleExpire]);
    const canSubmit = !SITE_KEY || !!captchaToken;
    async function handleSubmit(e) {
        e.preventDefault();
        if (!canSubmit)
            return;
        setError(null);
        setLoading(true);
        const { data, error: authError } = await supabase.auth.signInWithPassword({
            email: email.trim(),
            password,
            ...(SITE_KEY && captchaToken ? { options: { captchaToken } } : {}),
        });
        if (authError) {
            setError(parseAuthError(authError.message));
            setLoading(false);
            // Token is consumed by the failed attempt — reset widget so next try gets a fresh token
            const ts = window.turnstile;
            if (ts && widgetIdRef.current)
                ts.reset(widgetIdRef.current);
            setCaptchaToken(null);
            return;
        }
        // Fetch role and redirect to the right portal
        const { data: profile } = await supabase
            .from('profiles')
            .select('role, is_active')
            .eq('id', data.user.id)
            .single();
        if (profile?.is_active === false) {
            await supabase.auth.signOut();
            setError('Your account has been disabled. Please contact us for help.');
            setLoading(false);
            return;
        }
        if (profile?.role === 'admin') {
            const s = data.session;
            const hash = `access_token=${s.access_token}&refresh_token=${s.refresh_token}&expires_in=${s.expires_in}&token_type=bearer&type=bearer`;
            window.location.href = `https://team.springfielddogcentre.co.uk/admin#${hash}`;
        }
        else
            navigate('/portal', { replace: true });
    }
    return (_jsx("div", { className: "auth-page", children: _jsxs("div", { className: "auth-card", children: [_jsx("div", { className: "flex justify-center mb-6", children: _jsx("img", { src: "/images/logo-v2.webp", alt: "Springfield Dog Centre", className: "w-56" }) }), _jsx("h1", { children: "Log in" }), _jsxs("form", { onSubmit: handleSubmit, noValidate: true, children: [_jsxs("div", { className: "field", children: [_jsx("label", { htmlFor: "email", children: "Email address" }), _jsx("input", { id: "email", type: "email", autoComplete: "email", required: true, value: email, onChange: (e) => setEmail(e.target.value) })] }), _jsxs("div", { className: "field", children: [_jsx("label", { htmlFor: "password", children: "Password" }), _jsx("input", { id: "password", type: "password", autoComplete: "current-password", required: true, value: password, onChange: (e) => setPassword(e.target.value) })] }), SITE_KEY && (_jsx("div", { className: "flex justify-center my-4", children: _jsx("div", { ref: containerRef }) })), error && _jsx("p", { className: "error", children: error }), _jsx("button", { type: "submit", disabled: loading || !canSubmit, children: loading ? 'Logging in…' : 'Log in' })] }), _jsx("p", { children: _jsx(Link, { to: "/reset-password", children: "Forgotten your password?" }) }), _jsx("p", { children: _jsx(Link, { to: "/", children: "Back" }) })] }) }));
}
